import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useLanguageStore } from '../store/languageStore'
import { loc } from '../utils/localized'
import { loadPageSettings, savePageSettings, type PageSettingsValues } from '../api/pageSettingsApi'
import { defaultsForPage, type PageSettingsDef } from '../data/pageSettingsRegistry'
import { pageSettingKey } from '../data/pageSettingsRegistry'

export function pageSettingsQueryKey(pageId: string) {
  return ['page-settings', pageId] as const
}

/**
 * Load / save the JSON bag `Page.{pageId}` for a page definition.
 */
export function usePageSettings(def: PageSettingsDef | null | undefined) {
  const queryClient = useQueryClient()
  const { language } = useLanguageStore()
  const t = (ar: string, en: string, fr?: string) => loc(language, ar, en, fr)
  const pageId = def?.pageId

  const query = useQuery({
    queryKey: pageId ? pageSettingsQueryKey(pageId) : ['page-settings', 'none'],
    queryFn: () => loadPageSettings(def!),
    enabled: !!def,
    staleTime: 60_000,
  })

  const defaults = useMemo(() => (def ? defaultsForPage(def) : {}), [def])

  const values: PageSettingsValues = query.data ?? defaults

  const mutation = useMutation({
    mutationFn: (next: PageSettingsValues) => savePageSettings(def!, next),
    onSuccess: (_data, next) => {
      if (pageId) {
        queryClient.setQueryData(pageSettingsQueryKey(pageId), next)
      }
      toast.success(t('تم حفظ إعدادات الصفحة', 'Page settings saved', 'Paramètres de page enregistrés'))
    },
    onError: () => {
      toast.error(t('فشل حفظ إعدادات الصفحة', 'Failed to save page settings', 'Échec de l\'enregistrement des paramètres de page'))
    },
  })

  const get = useCallback(
    <T extends boolean | number | string>(key: string, fallback?: T): T => {
      const v = values[key]
      if (v === undefined || v === null) {
        return (fallback ?? defaults[key]) as T
      }
      return v as T
    },
    [values, defaults]
  )

  const setMany = useCallback(
    (patch: PageSettingsValues) => {
      if (!def) return
      const next = { ...values, ...patch }
      mutation.mutate(next)
    },
    [def, values, mutation]
  )

  const setOne = useCallback(
    (key: string, value: boolean | number | string) => {
      setMany({ [key]: value })
    },
    [setMany]
  )

  return {
    pageId,
    settingKey: pageId ? pageSettingKey(pageId) : null,
    values,
    defaults,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    get,
    setOne,
    setMany,
    save: setMany,
    refetch: query.refetch,
  }
}

/**
 * Convenience: read a single page setting by page def + key.
 */
export function usePageSettingValue<T extends boolean | number | string>(
  def: PageSettingsDef | null | undefined,
  key: string,
  fallback: T
): T {
  const { get, isLoading } = usePageSettings(def)
  if (isLoading && def) return fallback
  return get(key, fallback)
}
